import { useState, useContext } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttPrivilegeList,
    SttFormSwitch,
} from '../../../sporttia/all';
import useScsService from '../../../services/ScsService';
import { SPORTCENTER_CONFIGURATION_MANDATORYDATA } from '../../../lib/queryKeys';
import { useCanModifyConfiguration } from './useCanModifyConfiguration';

type SC = {
    id?: number;
    updatePursesOnCascadeAllowed?: boolean;
    userNameMandatory?: boolean;
    userSurname1Mandatory?: boolean;
    userSurname2Mandatory?: boolean;
    userBirthdayMandatory?: boolean;
    userPhoneMandatory?: boolean;
    userEmailMandatory?: boolean;
    userDniMandatory?: boolean;
    userAddressMandatory?: boolean;
    userPostalCodeMandatory?: boolean;
    userCityMandatory?: boolean;
    userIbanMandatory?: boolean;
    [key: string]: unknown;
};

type SCResponse = {
    sc: SC;
};

type Privilege = {
    id: string;
    name: string;
    description?: string;
    active: boolean;
};

type MutationParams = {
    [key: string]: unknown;
};

export default function ConfigurationMandatoryData(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const canModify = useCanModifyConfiguration();
    const [sc, setSC] = useState<SC | undefined>(cxt?.sc);
    const scsService = useScsService();
    useQuery({
        queryKey: [SPORTCENTER_CONFIGURATION_MANDATORYDATA, { id: sc?.id }],
        queryFn: () => {
            if (!sc?.id) return Promise.reject(new Error('No SC ID'));
            return scsService
                .get<SCResponse>(sc.id)
                .then((response: SCResponse) => {
                    setSC(response?.sc);
                });
        },
        enabled: !!sc?.id,
    });

    const mandatoryDataMutation = useMutation<
        SCResponse,
        { error?: string },
        MutationParams
    >({
        mutationFn: (params: MutationParams) => {
            if (!cxt?.sc?.id) return Promise.reject(new Error('No SC ID'));
            return scsService.updateScsField(
                cxt.sc.id,
                params,
            ) as Promise<SCResponse>;
        },
        onSuccess: (data: SCResponse) => {
            setSC(data.sc);
        },
        onError: (err: { error?: string }) =>
            cxt?.showMessage('E', err?.error || 'Error'),
    });

    // Adapt data format to expected fields for STTPrivilegeList component reusability
    const zip = (a: string[], b: string[]): Privilege[] =>
        a.map((aField, aIndexField) => ({
            id: aField,
            name: b[aIndexField] || '',
            active: (sc?.[aField] as boolean) || false,
        }));

    const userFields = [
        'userNameMandatory',
        'userSurname1Mandatory',
        'userSurname2Mandatory',
        'userBirthdayMandatory',
        'userPhoneMandatory',
        'userEmailMandatory',
        'userDniMandatory',
        'userAddressMandatory',
        'userPostalCodeMandatory',
        'userCityMandatory',
        'userIbanMandatory',
    ];

    const userFieldsTranslation = [
        t('Name'),
        t('Surname1'),
        t('Surname2'),
        t('Birthday'),
        t('Phone'),
        t('Email'),
        t('DNI'),
        t('Address'),
        t('PostalCode'),
        t('City'),
        t('IBAN'),
    ];

    const mandatoryFields = zip(userFields, userFieldsTranslation);

    // -----| Render |-----
    return (
        <>
            <SttFormSwitch
                color="primary"
                caption={t('autoUserDataSaving')}
                name="updatePursesOnCascadeAllowed"
                disabled={!canModify}
                checked={sc?.updatePursesOnCascadeAllowed || false}
                onChange={({ name, value }) => {
                    if (!name) return;
                    mandatoryDataMutation.mutate({ [name]: value });
                }}
            />
            <SttPrivilegeList
                privileges={mandatoryFields}
                onToggle={(data: { id: string; value: unknown }) => {
                    // SttPrivilegeList no sabe desactivar sus interruptores:
                    // sin permiso para modificar, el cambio se ignora.
                    if (!canModify) return;
                    mandatoryDataMutation.mutate({ [data.id]: data.value });
                }}
                title={t('Data')}
                useHighlight
            />
        </>
    );
}
